import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

const AREA_WAVES = [["shiga"], ["kyoto", "mie"], ["osaka", "aichi"], ["shizuoka"]];
const AREA_ORDER = AREA_WAVES.flat();
const ANIM_OBSERVER_OPTIONS = {
  rootMargin: "0px 0px -100px 0px",
  threshold: 0,
};
const ANIME_READY_CLASSES = {
  fade: "js-anime-ready-fade",
  move: "js-anime-ready-move",
  split: "js-anime-ready-split",
};
const ANIME_READY_NAMES = Object.values(ANIME_READY_CLASSES);

const ANIME_SETTINGS = {
  "fv-image": {
    // data-zoom="delayed" の時だけ拡大の開始を遅らせる。終わる時刻は scaleDuration のまま動かさない
    delay: 0.6,
    ease: "power1.out",
    scale: 1.08,
    scaleDuration: 6,
  },
  "fv-copy": {
    delay: 0.1,
    duration: 0.6,
    ease: "power2.out",
    lineOverlap: 0.15,
    stagger: 0.05,
  },
  "fv-text": {
    duration: 1.2,
    ease: "power2.out",
    shift: {
      desktop: { duration: 1.4, ease: "sine.out" },
    },
  },
  intro: {
    duration: 0.6,
    ease: "power4.out",
    stagger: 0.26,
    y: 60,
  },
  // 下層ページ用。動かす要素そのものを基準にし、画面下端から100px内側へ入ったら1回だけ動かす
  "fade-up": {
    duration: 0.6,
    ease: "power4.out",
    start: "top bottom-=100",
    y: 30,
  },
  service: {
    duration: 0.9,
    ease: "power2.out",
    stagger: 0.14,
    start: "top 85%",
    shift: {
      desktop: { x: 0, y: 0 },
      mobile: { static: true, x: 0, y: 0 },
    },
  },
  area: {
    duration: 0.22,
    ease: "power2.out",
    start: "top 88%",
    waveGap: 0.13,
    waveStart: 0.4,
    blur: 8,
    blurDuration: 1,
    blurEase: "power3.inOut",
  },
  philosophy: {
    duration: 0.45,
    ease: "power2.out",
    stagger: 0.1,
    start: "top 50%",
    shift: {
      desktop: { y: 30 },
      mobile: { static: true, y: 0 },
    },
  },
  licenses: {
    duration: 0.45,
    ease: "power2.out",
    stagger: 0.1,
    start: "top bottom-=100",
    y: 30,
  },
};

export const animeSettings = (name, desktop = true) => {
  const settings = ANIME_SETTINGS[name];

  if (!settings) {
    return null;
  }

  const shift = settings.shift?.[desktop ? "desktop" : "mobile"];
  const result = { ...settings, ...shift };

  delete result.shift;

  return result;
};

export const motionMode = reduced => (reduced ? "reduced" : "full");

export const animeQueries = () => ({
  desktop: "(min-width: 768px)",
  mobile: "(max-width: 767px)",
  reduced: "(prefers-reduced-motion: reduce)",
});

export const philosophyTrigger = group => group.querySelector(".js-anime-philosophy") ?? group;

export const flowMode = (desktop, reduced) => (desktop && !reduced ? "scroll" : "static");

export const flowRange = ({ firstStart, firstWidth, lastStart, lastWidth, stageWidth }) => {
  const firstCenter = firstStart + firstWidth / 2;
  const lastCenter = lastStart + lastWidth / 2;
  const stageCenter = stageWidth / 2;

  return {
    distance: Math.max(0, lastCenter - firstCenter),
    from: stageCenter - firstCenter,
    to: stageCenter - lastCenter,
  };
};

export const flowIndex = (progress, count) => {
  if (count < 1) {
    return -1;
  }

  const safeProgress = Math.min(1, Math.max(0, progress));

  return Math.round(safeProgress * (count - 1));
};

export const setCurrent = (cards, currentIndex) => {
  cards.forEach((card, index) => {
    card.dataset.current = String(index === currentIndex);
  });
};

export const areaMap = (root, desktop) => {
  const view = desktop ? "pc" : "sp";

  return root.querySelector(`.js-anime-area-map[data-view="${view}"]`);
};

export const areaBlurTarget = (root, desktop) => areaMap(root, desktop)?.querySelector(".js-anime-area-blur") ?? null;

export const areaWaves = (root, desktop) => {
  const map = areaMap(root, desktop);

  if (!map) return [];

  return AREA_WAVES.map(names => names.flatMap(name => [...map.querySelectorAll(`[data-prefecture="${name}"]`)]));
};

export const areaTargets = (root, desktop) => areaWaves(root, desktop).flat();

const clearTargets = targets => {
  targets.forEach(target => {
    target.classList.remove(...ANIME_READY_NAMES);
  });
};

const finishArea = root => {
  const group = root.querySelector(".js-anime-area");
  const paths = group ? [...group.querySelectorAll("[data-prefecture]")] : [];
  const blurTargets = group ? [...group.querySelectorAll(".js-anime-area-blur")] : [];

  if (paths.length) {
    gsap.set(paths, { clearProps: "fill" });
  }

  if (group) {
    gsap.set(group, { clearProps: "filter" });
  }

  if (blurTargets.length) {
    gsap.set(blurTargets, { clearProps: "filter" });
  }
};

const initArea = (root, desktop) => {
  const group = root.querySelector(".js-anime-area");
  const blurTarget = areaBlurTarget(root, desktop);
  const waves = areaWaves(root, desktop);
  const paths = waves.flat();
  const settings = animeSettings("area", desktop);

  if (!group || !blurTarget || paths.length !== AREA_ORDER.length || waves.some(wave => !wave.length) || !settings) {
    finishArea(root);
    return () => {};
  }

  const styles = getComputedStyle(document.documentElement);
  const base = styles.getPropertyValue("--color-bg-sub").trim();
  const accent = styles.getPropertyValue("--color-accent").trim();
  gsap.set(paths, { fill: base });
  gsap.set(blurTarget, { filter: `blur(${settings.blur}px)` });

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: group,
      start: settings.start,
      once: true,
    },
    onComplete: () => finishArea(root),
  });

  timeline.to(
    blurTarget,
    {
      filter: "blur(0px)",
      duration: settings.blurDuration,
      ease: settings.blurEase,
    },
    0,
  );

  waves.forEach((wave, index) => {
    timeline.to(
      wave,
      {
        fill: accent,
        duration: settings.duration,
        ease: settings.ease,
      },
      settings.waveStart + index * settings.waveGap,
    );
  });

  return () => {
    timeline.scrollTrigger?.kill();
    timeline.kill();
    finishArea(root);
  };
};

const readyTargets = (targets, state) => {
  const readyClass = ANIME_READY_CLASSES[state];

  if (!readyClass) return;

  targets.forEach(target => {
    target.classList.add(readyClass);
  });
};

const imageReady = (image, start) => {
  let active = true;
  let started = false;

  const clear = () => {
    image.removeEventListener("load", play);
    image.removeEventListener("error", play);
  };

  const play = () => {
    if (!active || started) return;

    started = true;
    clear();
    start();
  };

  if (image.complete) {
    play();
  } else {
    image.addEventListener("load", play, { once: true });
    image.addEventListener("error", play, { once: true });
  }

  return () => {
    active = false;
    clear();
  };
};

const allTargets = root => {
  const selectors = [".js-anime-fv-image", ".js-anime-fv-image img", ".js-anime-fv-copy", ".js-anime-fv-text", ".js-anime-service", ".js-anime-philosophy", ".js-anime-company-intro", ...ANIME_READY_NAMES.map(name => `.${name}`)];

  return [...new Set(root.querySelectorAll(selectors.join(",")))];
};

const finishTargets = root => {
  clearTargets(allTargets(root));
};

const initFv = (root, desktop) => {
  const figure = root.querySelector(".js-anime-fv-image");
  const image = figure?.querySelector("img");
  const copy = root.querySelector(".js-anime-fv-copy-trigger");
  const items = copy ? [...copy.querySelectorAll(".js-anime-fv-copy")] : [];
  const text = root.querySelector(".js-anime-fv-text");

  if (!figure || !image) {
    clearTargets([...items, text].filter(Boolean));
    return () => {};
  }

  const imageSettings = animeSettings("fv-image");
  const copySettings = animeSettings("fv-copy");
  const textSettings = animeSettings("fv-text", desktop);
  const targets = [image, ...items, text].filter(Boolean);
  let timeline = null;
  let splits = [];
  let splitChars = [];
  let splitFinished = false;

  const finishSplit = () => {
    if (splitFinished) return;

    splitFinished = true;
    clearTargets([...items, ...splitChars]);
    splits.forEach(split => split.revert());
    items.forEach(item => {
      item.style.removeProperty("opacity");
      item.style.removeProperty("visibility");
    });
    splits = [];
    splitChars = [];
  };

  readyTargets(items, "split");
  readyTargets([image], "move");
  if (text) readyTargets([text], "fade");

  const stopReady = imageReady(image, () => {
    splits = items.map(item => SplitText.create(item, { aria: "auto", charsClass: "js-split-char", tag: "span", type: "chars" }));
    splitChars = splits.flatMap(split => split.chars);
    items.forEach(item => {
      item.style.opacity = "1";
    });

    timeline = gsap.timeline({
      onComplete: () => clearTargets(targets),
    });
    const zoomStart = figure.dataset.zoom === "delayed" ? imageSettings.delay : 0;

    timeline.addLabel("fv-start", 0).addLabel("fv-copy", copySettings.delay);
    timeline.fromTo(
      image,
      { scale: 1 },
      {
        scale: imageSettings.scale,
        duration: imageSettings.scaleDuration - zoomStart,
        ease: imageSettings.ease,
      },
      zoomStart,
    );

    let copyEnd = copySettings.delay;

    splits.forEach((split, index) => {
      const lineStart = index === 0 ? copySettings.delay : copyEnd - copySettings.lineOverlap;

      timeline.fromTo(
        split.chars,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: copySettings.duration,
          ease: copySettings.ease,
          stagger: copySettings.stagger,
        },
        lineStart,
      );
      copyEnd = lineStart + copySettings.duration + copySettings.stagger * Math.max(0, split.chars.length - 1);
    });

    timeline.addLabel("fv-text", copyEnd).call(finishSplit, [], "fv-text");

    if (text && textSettings) {
      timeline.fromTo(
        text,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: textSettings.duration,
          ease: textSettings.ease,
        },
        "fv-text",
      );
    }
  });

  return () => {
    stopReady();
    timeline?.kill();
    finishSplit();
    clearTargets(targets);
  };
};

const clearFv = root => {
  const image = root.querySelector(".js-anime-fv-image img");

  if (image) gsap.set(image, { clearProps: "scale,transform,willChange" });
};

const introItems = root => [...root.querySelectorAll(".js-anime-company-intro")];

const clearIntro = root => {
  const items = introItems(root);

  if (items.length) gsap.set(items, { clearProps: "flexBasis,willChange" });
};

const initIntro = root => {
  const group = root.querySelector(".js-anime-company-intro-trigger");
  const items = introItems(root);

  if (!group || items.length !== 3) return () => {};

  const [left, center, right] = items;
  const groupWidth = group.clientWidth;
  const cssOpenBasis = Number.parseFloat(getComputedStyle(left).flexBasis);
  const cssClosedBasis = Number.parseFloat(getComputedStyle(center).flexBasis);
  const openBasis = groupWidth > 0 && Number.isFinite(cssOpenBasis) ? `${(cssOpenBasis / groupWidth) * 100}%` : "80%";
  const closedBasis = groupWidth > 0 && Number.isFinite(cssClosedBasis) ? `${(cssClosedBasis / groupWidth) * 100}%` : "10%";

  // 発火時の切り替えを見せないよう、スクロール前に右端を開いておく
  gsap.set(items, { willChange: "flex-basis" });
  gsap.set([left, center], { flexBasis: closedBasis });
  gsap.set(right, { flexBasis: openBasis });

  const timeline = gsap.timeline({
    defaults: {
      duration: 0.9,
      ease: "power2.inOut",
    },
    scrollTrigger: {
      trigger: group,
      start: "top 80%",
      once: true,
    },
    onComplete: () => clearIntro(root),
  });

  timeline.to(center, { flexBasis: openBasis }, "+=0.25").to(right, { flexBasis: closedBasis }, "<").to(left, { flexBasis: openBasis }, "+=0.25").to(center, { flexBasis: closedBasis }, "<");

  return () => {
    timeline.scrollTrigger?.kill();
    timeline.kill();
    clearIntro(root);
  };
};

const visibleItems = items => {
  const active = items.filter(item => item.classList.contains("is-active") || item.classList.contains("is-visible"));

  return active.length ? active : items.slice(0, 1);
};

export const serviceOffsets = cards => {
  const starts = cards.map(card => card.closest(".js-anime-service")?.offsetLeft ?? 0);
  const origin = starts[0] ?? 0;

  return starts.map(start => origin - start);
};

const finishService = cards => {
  if (cards.length) {
    gsap.set(cards, { clearProps: "transform,zIndex,willChange" });
  }

  clearTargets(cards);
};

const initServiceDeal = (group, cards, settings) => {
  if (cards.length < 2) return () => {};

  const movingCards = cards.slice(1);
  const offsets = serviceOffsets(cards).slice(1);

  readyTargets(movingCards, "move");
  gsap.set(cards[0], { zIndex: cards.length });

  const tween = gsap.fromTo(
    movingCards,
    {
      x: index => offsets[index],
      zIndex: index => cards.length - index - 1,
    },
    {
      x: 0,
      duration: settings.duration,
      ease: settings.ease,
      stagger: settings.stagger,
      scrollTrigger: {
        trigger: group,
        start: settings.start,
        once: true,
      },
      onComplete: () => finishService(cards),
    },
  );

  return () => {
    tween.scrollTrigger?.kill();
    tween.kill();
    finishService(cards);
  };
};

const animeItems = (name, found, desktop) => {
  const items = name === "service" && !desktop ? visibleItems(found) : found;

  if (name === "service") {
    return items.map(item => item.querySelector(".js-anime-service-card")).filter(Boolean);
  }

  return items;
};

const flowElements = root => {
  const group = root.querySelector(".js-anime-flow-group");
  const stage = root.querySelector(".js-anime-flow-stage");
  const list = stage?.querySelector(".js-anime-flow-list");
  const items = list ? [...list.querySelectorAll(".js-anime-flow-item")] : [];
  const cards = items.map(item => item.querySelector(".js-anime-flow-card")).filter(Boolean);

  return { cards, group, items, list, stage };
};

export const finishFlow = root => {
  const { cards, list } = flowElements(root);

  if (list) {
    gsap.set(list, { clearProps: "transform,willChange" });
  }

  setCurrent(cards, null);
};

const initScroll = root => {
  // js-anime-flow が無いときは PC ピン留め横スクロールを起動しない
  if (!root.querySelector(".js-anime-flow")) {
    finishFlow(root);
    return () => {};
  }

  const { cards, group, items, list, stage } = flowElements(root);

  if (!group || !stage || !list || items.length < 2 || cards.length !== items.length) {
    finishFlow(root);
    return () => {};
  }

  const range = () => {
    const first = items[0];
    const last = items.at(-1);

    return flowRange({
      firstStart: first.offsetLeft,
      firstWidth: first.offsetWidth,
      lastStart: last.offsetLeft,
      lastWidth: last.offsetWidth,
      stageWidth: stage.clientWidth,
    });
  };

  gsap.set(list, { willChange: "transform" });
  setCurrent(cards, 0);

  const tween = gsap.fromTo(
    list,
    { x: () => range().from },
    {
      x: () => range().to,
      ease: "none",
      scrollTrigger: {
        trigger: group,
        start: "top top",
        end: () => `+=${Math.max(window.innerHeight, range().distance)}`,
        scrub: true,
        pin: group,
        invalidateOnRefresh: true,
        onRefresh: self => setCurrent(cards, flowIndex(self.progress, cards.length)),
        onUpdate: self => setCurrent(cards, flowIndex(self.progress, cards.length)),
      },
    },
  );

  return () => {
    tween.scrollTrigger?.kill();
    tween.kill();
    finishFlow(root);
  };
};

export const syncPanels = (panels, desktop) => {
  panels.forEach((panel, index) => {
    panel.open = desktop || index === 0;
  });

  return panels;
};

export const holdPanel = event => {
  const panel = event.currentTarget.closest("details");

  if (panel?.open) {
    event.preventDefault();
  }
};

const initPanels = panels => {
  const summaries = panels.map(panel => panel.querySelector("summary")).filter(Boolean);

  summaries.forEach(summary => summary.addEventListener("click", holdPanel));

  return () => {
    summaries.forEach(summary => summary.removeEventListener("click", holdPanel));
  };
};

const fadeInUpsTargets = root => [...root.querySelectorAll(".js-fadeIn-ups")];

export const fadeInUpsWatchTargets = (trigger, desktop) => (desktop ? [trigger] : fadeInUpsTargets(trigger));

const clearFadeInUps = root => {
  const targets = fadeInUpsTargets(root);

  if (targets.length) {
    gsap.set(targets, { clearProps: "opacity,transform,visibility,willChange" });
    clearTargets(targets);
  }
};

const initFadeInUps = (root, desktop) => {
  const triggers = [...root.querySelectorAll(".js-fadeIn-ups-trigger")];
  const settings = animeSettings("intro");
  const tweens = [];

  if (!triggers.length || !settings) return () => {};

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;

      play(entry.target);
    });
  }, ANIM_OBSERVER_OPTIONS);

  const play = observedTarget => {
    const targets = desktop ? fadeInUpsTargets(observedTarget) : [observedTarget];

    targets.forEach((target, index) => {
      const tween = gsap.fromTo(
        target,
        { autoAlpha: 0, y: settings.y },
        {
          autoAlpha: 1,
          y: 0,
          duration: settings.duration,
          ease: settings.ease,
          delay: desktop ? settings.stagger * index : 0,
          onComplete: () => {
            gsap.set(target, { clearProps: "opacity,transform,visibility,willChange" });
            clearTargets([target]);
          },
        },
      );

      tweens.push(tween);
    });

    observer.unobserve(observedTarget);
  };

  triggers.forEach(trigger => {
    const targets = fadeInUpsTargets(trigger);

    readyTargets(targets, "fade");

    fadeInUpsWatchTargets(trigger, desktop).forEach(watchTarget => {
      if (watchTarget.getBoundingClientRect().top < window.innerHeight - 100) {
        play(watchTarget);
      } else {
        observer.observe(watchTarget);
      }
    });
  });

  return () => {
    observer.disconnect();
    tweens.forEach(tween => tween.kill());
    clearFadeInUps(root);
  };
};

// 下層ページのフェードアップ。要素ごとに自分自身をトリガーへ渡すため、監視役の置き場所で発火位置がずれない
const initFadeUp = root => {
  const items = [...root.querySelectorAll(".js-anime-fade-up")];
  const settings = animeSettings("fade-up");

  if (!items.length || !settings) return () => {};

  readyTargets(items, "fade");

  const tweens = items.map(item =>
    gsap.fromTo(
      item,
      { autoAlpha: 0, y: settings.y },
      {
        autoAlpha: 1,
        y: 0,
        duration: settings.duration,
        ease: settings.ease,
        scrollTrigger: {
          trigger: item,
          start: settings.start,
          once: true,
        },
        onComplete: () => {
          gsap.set(item, { clearProps: "opacity,transform,visibility,willChange" });
          clearTargets([item]);
        },
      },
    ),
  );

  return () => {
    tweens.forEach(tween => {
      tween.scrollTrigger?.kill();
      tween.kill();
    });
    gsap.set(items, { clearProps: "opacity,transform,visibility,willChange" });
    clearTargets(items);
  };
};

const scrollAnime = (root, name, desktop) => {
  const group = root.querySelector(`.js-anime-${name}-trigger`);
  const settings = animeSettings(name, desktop);

  if (!group || !settings || settings.static) {
    return () => {};
  }

  const found = [...group.querySelectorAll(`.js-anime-${name}`)];
  const items = animeItems(name, found, desktop);

  if (!items.length) {
    return () => {};
  }

  if (name === "service" && desktop) {
    return initServiceDeal(group, items, settings);
  }

  const hasFade = name !== "flow";
  const trigger = name === "philosophy" ? philosophyTrigger(group) : group;

  readyTargets(items, hasFade ? "fade" : "move");

  const from = {
    scale: settings.scale ?? 1,
    x: settings.x ?? 0,
    y: settings.y ?? 0,
  };
  const to = {
    scale: 1,
    x: 0,
    y: 0,
    duration: settings.duration,
    ease: settings.ease,
    stagger: settings.stagger,
    scrollTrigger: {
      trigger,
      start: settings.start,
      once: true,
      pinnedContainer: name === "philosophy" && desktop ? root.querySelector(".js-anime-flow-group") : null,
    },
    onComplete: () => clearTargets(items),
  };

  if (hasFade) {
    from.autoAlpha = 0;
    to.autoAlpha = 1;
  }

  const tween = gsap.fromTo(items, from, to);

  return () => {
    tween.scrollTrigger?.kill();
    tween.kill();
    gsap.set(items, { clearProps: "opacity,transform,visibility,willChange" });
    clearTargets(items);
  };
};

export const initAnime = ({ root = document } = {}) => {
  const media = gsap.matchMedia();
  const cleanups = [];
  const panels = [...root.querySelectorAll(".js-anime-philosophy-panel")];
  let panelMode = null;

  cleanups.push(initPanels(panels));

  media.add(animeQueries(), context => {
    const { desktop, reduced } = context.conditions;

    if (panelMode !== desktop) {
      syncPanels(panels, desktop);
      panelMode = desktop;
    }

    if (motionMode(reduced) === "reduced") {
      finishTargets(root);
      finishFlow(root);
      finishArea(root);
      clearFv(root);
      clearIntro(root);
      clearFadeInUps(root);
      return () => {
        finishTargets(root);
        finishFlow(root);
        finishArea(root);
        clearFv(root);
        clearIntro(root);
        clearFadeInUps(root);
      };
    }

    const tasks = [initFv(root, desktop), initIntro(root), initArea(root, desktop), initFadeInUps(root, desktop), scrollAnime(root, "service", desktop)];

    if (flowMode(desktop, reduced) === "scroll") {
      tasks.push(initScroll(root));
    } else {
      finishFlow(root);
    }

    tasks.push(scrollAnime(root, "philosophy", desktop));
    tasks.push(scrollAnime(root, "licenses", desktop));
    tasks.push(initFadeUp(root));

    return () => {
      tasks.forEach(task => task());
      finishTargets(root);
      finishFlow(root);
      finishArea(root);
    };
  });

  const refresh = () => ScrollTrigger.refresh();

  if (document.readyState === "complete") {
    refresh();
  } else {
    window.addEventListener("load", refresh, { once: true });
    cleanups.push(() => window.removeEventListener("load", refresh));
  }

  return () => {
    cleanups.forEach(cleanup => cleanup());
    media.revert();
    finishTargets(root);
    finishFlow(root);
    finishArea(root);
    clearFv(root);
    clearIntro(root);
    clearFadeInUps(root);
  };
};

if (typeof document !== "undefined" && typeof window !== "undefined") {
  initAnime();
}
